import { CheckCircle } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "./Button";
import { Chip } from "./Chip";
import { ProgressMeter } from "./ProgressMeter";

export type StepRailStep = {
  id: string;
  name: string;
  description: string;
  icon: LucideIcon;
};

export function StepRail({
  steps,
  currentStep,
  onStep,
  status,
}: {
  steps: StepRailStep[];
  currentStep: number;
  onStep: (step: number) => void;
  status?: { label: string; tone?: "success" | "warning" | "danger" };
}) {
  const timelineProgress = steps.length > 1 ? currentStep / (steps.length - 1) : 1;
  const current = steps[currentStep];
  const statusTone =
    status?.tone === "danger" ? "danger" : status?.tone === "warning" ? "warning" : "success";

  return (
    <nav
      aria-label="Post job steps"
      className="card-hairline [contain:paint] p-4 lg:sticky lg:top-24"
    >
      <div className="lg:hidden">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="page-eyebrow">
              Step {currentStep + 1} of {steps.length}
            </p>
            <p className="mt-1 font-sans text-lg font-semibold text-text">{current.name}</p>
          </div>
          <div className="flex gap-2">
            <Button
              disabled={currentStep === 0}
              size="sm"
              type="button"
              variant="ghost"
              onClick={() => onStep(Math.max(0, currentStep - 1))}
            >
              Back
            </Button>
            <Button
              disabled={currentStep === steps.length - 1}
              size="sm"
              type="button"
              variant="primary"
              onClick={() => onStep(Math.min(steps.length - 1, currentStep + 1))}
            >
              Next
            </Button>
          </div>
        </div>
        {status ? (
          <div className="mt-3">
            <Chip tone={statusTone}>{status.label}</Chip>
          </div>
        ) : null}
        <div className="mt-4">
          <ProgressMeter
            label="Post job step progress"
            value={((currentStep + 1) / steps.length) * 100}
          />
        </div>
        <ol className="mt-4 flex flex-wrap gap-2 pb-1">
          {steps.map((step, index) => {
            const Icon = step.icon;

            return (
              <li key={step.id} className="shrink-0">
                <button
                  aria-label={`Go to step ${index + 1}`}
                  aria-current={currentStep === index ? "step" : undefined}
                  className={`min-h-11 scroll-mt-28 rounded-pill border px-4 text-sm font-semibold transition ${
                    currentStep === index
                      ? "border-accent-200 bg-accent-50 text-accent-700 shadow-[var(--shadow-card)]"
                      : "border-border bg-surface-raised text-text-muted"
                  }`}
                  type="button"
                  onClick={() => onStep(index)}
                >
                  <span className="inline-flex items-center gap-2">
                    <Icon aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
                    {step.name}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
      <div className="hidden lg:block">
        <div className="mb-4">
          <p className="page-eyebrow">Customer flow</p>
          {status ? (
            <div className="mt-3">
              <Chip tone={statusTone}>{status.label}</Chip>
            </div>
          ) : null}
        </div>
        <ol className="relative grid gap-3">
          <span aria-hidden="true" className="absolute bottom-8 left-5 top-8 w-0.5 bg-border" />
          <span
            aria-hidden="true"
            className="absolute bottom-8 left-5 top-8 w-[3px] origin-top bg-accent-700 transition-transform duration-[var(--duration-spring)] ease-[var(--ease-spring)]"
            style={{ transform: `scaleY(${timelineProgress})` }}
          />
          {steps.map((step, index) => {
            const Icon = step.icon;
            const complete = currentStep > index;
            const active = currentStep === index;

            return (
              <li key={step.id} className="relative">
                <button
                  aria-current={active ? "step" : undefined}
                  className={`flex min-h-16 w-full items-start gap-3 rounded-[12px] border px-3 py-3 text-left text-sm font-semibold transition duration-200 ${
                    active
                      ? "border-transparent bg-surface-muted text-text shadow-none"
                      : "border-transparent bg-transparent text-text-muted hover:border-border hover:bg-surface-muted hover:text-text"
                  }`}
                  type="button"
                  aria-label={`Go to step ${index + 1}`}
                  onClick={() => onStep(index)}
                >
                  <span
                    aria-hidden="true"
                    className={`z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-md font-sans text-sm font-semibold ${
                      complete
                        ? "brand-mark text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.55)]"
                        : active
                          ? "border border-accent-300 bg-surface-raised text-accent-700 shadow-xs"
                          : "border border-border bg-surface-muted text-text"
                    }`}
                  >
                    {complete ? (
                      <CheckCircle aria-hidden="true" className="h-5 w-5" strokeWidth={1.5} />
                    ) : (
                      <Icon aria-hidden="true" className="h-5 w-5" strokeWidth={1.5} />
                    )}
                  </span>
                  <span className="min-w-0">
                    <span className="block card-title">{step.name}</span>
                    <span className="mt-1 block text-xs font-semibold text-text-muted">
                      {step.description}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
        <p className="cell-label mt-4 border-t border-border pt-3 font-mono">
          Draft auto-saves locally
        </p>
      </div>
    </nav>
  );
}
